import { Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import {
  DataRequestState,
  useDataRequest,
  useRomeisGridHandlers,
} from "../data";
import { useGetApi } from "../../client";
import { EingangsrechnungInfo } from "../../api";
import { useZahllaufContext } from "../../contexts/ZahllaufContext";
import {
  RomeisDataGrid,
  ColumnVisibilityModel,
  PivotModel,
  germanLabels,
} from "@romeis/romeis-data-grid";
import {
  ZahllaufFilter,
  ZahllaufProps,
  ZahllaufSummenProps,
  ZahllaufFilterBar,
  useZahllaufColumns,
} from "./ZahllaufCommon";
import { useNavigate } from "react-router";

export default function ZahllaufPivotTable({
  setAnzahlAlleRechnungen,
  setSummeAlleRechnungen,
  setAnzahlSelectedRechnungen,
  setSummeSelectedRechnungen,
  setAlleSelectedRechnungen,
  alleRechnungen,
  ...input
}: ZahllaufSummenProps & ZahllaufProps) {
  const [filteredState, setFilteredState] = useState("");
  const request = useDataRequest<ZahllaufFilter>({
    filter: { zahllaufBezahlt: false },
    ...input,
  });

  return (
    <Stack spacing={2}>
      <ZahllaufFilterBar
        request={request}
        filteredStateFilter={filteredState}
      />
      <ZahllaufResults
        setAnzahlAlleRechnungen={setAnzahlAlleRechnungen}
        setSummeAlleRechnungen={setSummeAlleRechnungen}
        setAnzahlSelectedRechnungen={setAnzahlSelectedRechnungen}
        setSummeSelectedRechnungen={setSummeSelectedRechnungen}
        setAlleSelectedRechnungen={setAlleSelectedRechnungen}
        request={request}
        setFilteredState={setFilteredState}
      />
    </Stack>
  );
}

function ZahllaufResults({
  setAnzahlAlleRechnungen,
  setSummeAlleRechnungen,
  setAnzahlSelectedRechnungen,
  setSummeSelectedRechnungen,
  setAlleSelectedRechnungen,
  request,
  setFilteredState,
}: ZahllaufSummenProps & {
  request: DataRequestState<ZahllaufFilter>;
  setFilteredState: (value: string) => void;
}) {
  return (
    <Results
      request={request}
      setFilteredState={setFilteredState}
      zahllaufprops={{
        setAnzahlAlleRechnungen,
        setSummeAlleRechnungen,
        setAnzahlSelectedRechnungen,
        setSummeSelectedRechnungen,
        setAlleSelectedRechnungen,
      }}
    />
  );
}

function Results({
  zahllaufprops,
  request,
  setFilteredState,
}: {
  zahllaufprops: ZahllaufSummenProps;
  request: DataRequestState<ZahllaufFilter>;
  setFilteredState: (value: string) => void;
}) {
  const getApi = useGetApi();
  const columns = useZahllaufColumns({ removeHyphensFromHeaders: true });
  const navigate = useNavigate();
  const [rechnungenIds, setRechnungenIds] = useState<string[]>([]);
  const [visibility, setVisibility] = useState<ColumnVisibilityModel>(() => {
    const visibleColumns = [
      "zahlungkw",
      "rechnungsNummer",
      "gutschriftenRechnungsnummern",
    ];
    return Object.fromEntries(
      columns.map((c) => [c.id, visibleColumns.includes(c.id)])
    );
  });
  const { serverKeyVersion } = useZahllaufContext();
  const {
    pagination,
    filterModel,
    sortModel,
    onPageChange,
    onPageSizeChange,
    onFilterModelChange,
    onSortModelChange,
  } = useRomeisGridHandlers(request);

  const [pivotModel, setPivotModel] = useState<PivotModel>({
    rowFields: ["lieferantName"],
    columnFields: [],
    values: [
      { field: "nettoBetrag", aggregation: "sum" },
      { field: "umsatzsteuer", aggregation: "sum" },
      { field: "bruttoBetrag", aggregation: "sum" },
      { field: "skontoBetrag", aggregation: "sum" },
      { field: "rechnungsBetrag", aggregation: "sum" },
      { field: "gesamtGutschrift", aggregation: "sum" },
    ],
  });

  const { data: alleRechnungen } = useQuery(
    [
      "zahllauf",
      request.params,
      pagination.page,
      pagination.pageSize,
      filterModel,
      sortModel,
    ],
    async () => {
      const api = await getApi();
      const mappedFilters = filterModel.map((f) => ({
        field: f.columnId,
        operator: f.operator,
        value: f.value,
      }));
      return await api.rechnungen.sucheEingangsrechnungen({
        ...request.params,
        page: pagination.page,
        limit: pagination.pageSize,
        sort:
          sortModel?.direction === "desc"
            ? "-" + sortModel.columnId
            : sortModel?.columnId,
        filtered: JSON.stringify(mappedFilters),
      });
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );

  useEffect(() => {
    const mappedFilters = filterModel.map((f) => ({
      field: f.columnId,
      operator: f.operator,
      value: f.value,
    }));
    setFilteredState(JSON.stringify(mappedFilters));
  }, [filterModel, setFilteredState]);

  useEffect(() => {
    const anzahlAllerRechnungen = alleRechnungen?.totalElements ?? 0;
    const summeAllerRechnungen = alleRechnungen?.items.reduce(
      (acc, aktuellerWert) => acc + (aktuellerWert?.rechnungsBetrag ?? 0),
      0
    );
    const anzahlAusgewaehlterRechnungen = rechnungenIds.length;
    const summeAusgewaehlterRechnungen = alleRechnungen?.items
      .filter((rechnung) => rechnungenIds.includes(rechnung.id))
      .reduce(
        (acc, aktuellerWert) => acc + (aktuellerWert?.rechnungsBetrag ?? 0),
        0
      );

    const nichtAlleFreigegeben = alleRechnungen?.items
      .filter((rechnung) => rechnungenIds.includes(rechnung.id))
      .some(
        (rechnung) =>
          rechnung.belegStatus !== "FREIGEGEBEN" &&
          rechnung.belegStatus !== "IN_DATEV"
      );

    zahllaufprops.setAnzahlAlleRechnungen(anzahlAllerRechnungen ?? 0);
    zahllaufprops.setSummeAlleRechnungen(summeAllerRechnungen ?? 0);
    zahllaufprops.setAnzahlSelectedRechnungen(
      anzahlAusgewaehlterRechnungen ?? 0
    );
    zahllaufprops.setSummeSelectedRechnungen(summeAusgewaehlterRechnungen ?? 0);
    zahllaufprops.setBeinhaltetNichtFreigegebeneRechnungen?.(
      nichtAlleFreigegeben ?? true
    );
    zahllaufprops.setAlleSelectedRechnungen(rechnungenIds ?? []);
  }, [rechnungenIds, alleRechnungen, zahllaufprops]);

  return (
    <RomeisDataGrid<EingangsrechnungInfo>
      columns={columns}
      rows={alleRechnungen?.items}
      getRowId={(row) => row.id}
      pageSize={pagination.pageSize}
      paginationMode={"client"}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onSelectionChange={(newSelection) => {
        setRechnungenIds(newSelection as string[]);
      }}
      columnVisibilityModel={visibility}
      onColumnVisibilityModelChange={setVisibility}
      filterModel={filterModel}
      onFilterModelChange={onFilterModelChange}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      csvOptions={{ fileName: "zahllauf.csv" }}
      printOptions={{ title: "Zahlläufe", allPages: true }}
      showToolbar={false}
      pageSizeOptions={[50, 100, 200, 300, 500]}
      queryKey={[serverKeyVersion, request.params]}
      pivotModel={pivotModel}
      onPivotModelChange={setPivotModel}
      pivotEnabled={true}
      pivotShowLeafRows={true}
      labels={germanLabels}
      onRowClick={(row) => {
        if (row.event.ctrlKey || row.event.metaKey) {
          window.open(`/eingangsrechnungen/${row.id}`, "_blank");
        } else {
          navigate(`/eingangsrechnungen/${row.id}`);
        }
      }}
      hidePivotToggle={true}
    />
  );
}
